
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchAttendanceDefaulters } from "../redux/actions/ParkAttendanceActions";
import { fetchDistricts, fetchMunicipalities } from "../redux/actions/locationActions";
import { fetchDesignations } from "../redux/actions/designationActions";
import theme from "../components/shared/theme";
import { tdStyle, thStyle, paginationButtonStyle, dropdownStyle } from "../components/shared/themeStyles";
import Papa from "papaparse";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const itemsPerPage = 10;

const ParkAttendanceDefaulters = () => {
  const dispatch = useDispatch();

  // Use safe defaults in case redux slices are undefined
  const parkAttendanceState = useSelector((state) => state.parkAttendance || {});
  const { defaulters = [], loading = false, error = null, pagination = {} } = parkAttendanceState;

  const locationState = useSelector((state) => state.location || {});
  const { districts = [], municipalities = [] } = locationState;

  const designationState = useSelector((state) => state.designation || {});
  const { designations = [] } = designationState;

  // Filters & UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedProjectType, setSelectedProjectType] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(true);
  const [showAbsentDates, setShowAbsentDates] = useState({});

  // Fetch initial data for filters
  useEffect(() => {
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
    dispatch(fetchDesignations());
  }, [dispatch]);

  // Fetch defaulters whenever filters / page change
  useEffect(() => {
    dispatch(
      fetchAttendanceDefaulters({
        page: currentPage,
        limit: itemsPerPage,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        fromDate,
        toDate,
        search: searchTerm,
        designation: selectedDesignation,
        project_type: selectedProjectType,
      })
    );
  }, [
    dispatch,
    currentPage,
    selectedDistrict,
    selectedMunicipality,
    fromDate,
    toDate,
    searchTerm,
    selectedDesignation,
    selectedProjectType,
  ]);

  // Reset municipality when district changes
  useEffect(() => {
    setSelectedMunicipality("");
  }, [selectedDistrict]);

  // Helpers
  const formatDate = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("en-IN", { day: 'numeric', month: 'short' });
  };

  // Format absent dates for display
  const formatAbsentDatesDisplay = (dates) => {
    if (!dates || dates.length === 0) return "-";
    if (dates.length <= 3) {
      return dates.map(date => formatDate(date)).join(", ");
    }
    return `${formatDate(dates[0])}, ${formatDate(dates[1])}, +${dates.length - 2} more`;
  };

  // Format absent dates for CSV
  const formatAbsentDatesCSV = (dates) => {
    if (!dates || dates.length === 0) return "-";
    return dates.map(date => new Date(date).toLocaleDateString("en-IN")).join(", ");
  };

  // Toggle showing all absent dates for a specific row
  const toggleAbsentDates = (id) => {
    setShowAbsentDates(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // CSV Export in requested format
  const handleDownloadCSV = async () => {
    try {
      const token = localStorage.getItem("token") || "";
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        ...(selectedDistrict && { district: selectedDistrict }),
        ...(selectedMunicipality && { municipality: selectedMunicipality }),
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        ...(searchTerm && { search: searchTerm }),
        ...(selectedDesignation && { designation: selectedDesignation }),
        ...(selectedProjectType && { project_type: selectedProjectType }),
      }).toString();

      const response = await fetch(
        `${BASE_URL}/v1/api/admin/getAttendanceDefaulters?${queryParams}`,
        {
          headers: {
            authorization: token,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const json = await response.json();
      const rows = json?.data || [];

      const csvRows = [
        ["id", "District", "Name of Municipality", "Name", "Designation", "Email", "Phone", "Project Type", "Project Code", "Total Absent Days", "Absent Dates"],
      ];

      rows.forEach((def) => {
        csvRows.push([
          def.id,
          def.district,
          def.municipality,
          def.name,
          def.designation,
          def.email || "",
          def.phone,
          def.project_type || "",
          def.project_code || "",
          def.total_absent_days || 0,
          formatAbsentDatesCSV(def.absent_dates),
        ]);
      });

      const csv = Papa.unparse(csvRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      const filename = `Attendance_Defaulters_${fromDate || 'all'}_to_${toDate || 'all'}.csv`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("CSV download error:", err);
      alert("CSV download failed. Please try again.");
    }
  };

  // Calculate date range label
  const getDateRangeLabel = () => {
    if (fromDate && toDate) {
      return `${new Date(fromDate).toLocaleDateString("en-IN", { day: 'numeric', month: 'short' })} to ${new Date(toDate).toLocaleDateString("en-IN", { day: 'numeric', month: 'short' })}`;
    } else if (fromDate) {
      return `From ${new Date(fromDate).toLocaleDateString("en-IN", { day: 'numeric', month: 'short' })}`;
    } else if (toDate) {
      return `To ${new Date(toDate).toLocaleDateString("en-IN", { day: 'numeric', month: 'short' })}`;
    }
    return "All Dates";
  };

  // Count active filters
  const countActiveFilters = () => {
    let count = 0;
    if (selectedDistrict) count++;
    if (selectedMunicipality) count++;
    if (selectedDesignation) count++;
    if (selectedProjectType) count++;
    if (searchTerm) count++;
    if (fromDate || toDate) count++;
    return count;
  };

  return (
    <div
      style={{
        padding: theme.spacing.lg,
        backgroundColor: theme.colors.background,
        minHeight: "100vh",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .status-badge {
          padding: 3px 8px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 600;
          display: inline-block;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        
        .park-badge { 
          background: linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%); 
          color: white; 
        }
        .shelter-badge { 
          background: linear-gradient(135deg, #2196F3 0%, #0D47A1 100%); 
          color: white; 
        }
        .stretch-badge { 
          background: linear-gradient(135deg, #FF9800 0%, #EF6C00 100%); 
          color: white; 
        }
        .default-badge { 
          background: linear-gradient(135deg, #9E9E9E 0%, #616161 100%); 
          color: white; 
        }
        
        .table-row {
          transition: all 0.2s ease;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .table-row:hover {
          background: linear-gradient(90deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%);
        }
        
        .absent-date-chip {
          background: linear-gradient(135deg, #fed7d7 0%, #feb2b2 100%);
          color: #c53030;
          padding: 1px 4px;
          border-radius: 6px;
          font-size: 9px;
          font-weight: 500;
          margin: 0 1px 1px 0;
          border: 1px solid #fc8181;
          white-space: nowrap;
          display: inline-block;
          line-height: 1.3;
        }
        
        .loading-shimmer {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        .table-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border: 1px solid #e2e8f0;
          overflow: hidden;
          animation: fadeIn 0.6s ease;
        }
        
        .mobile-hidden {
          display: none;
        }
        
        @media (min-width: 1024px) {
          .mobile-hidden {
            display: table-cell;
          }
        }
        
        .show-more-btn {
          background: none;
          border: none;
          color: #667eea;
          font-size: 9px;
          cursor: pointer;
          padding: 1px 4px;
          margin-left: 2px;
          text-decoration: underline;
          display: inline-block;
          white-space: nowrap;
        }
        
        .compact-cell {
          max-width: 120px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .email-cell {
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .absent-dates-container {
          display: inline-flex;
          flex-wrap: wrap;
          gap: 1px;
          align-items: center;
          line-height: 1.2;
        }
        
        .absent-dates-cell {
          padding: 8px 4px !important;
          max-width: 180px;
          min-width: 150px;
        }
        
        .pagination-container {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: nowrap;
        }
        
        .pagination-info {
          font-size: 11px;
          color: #4a5568;
          white-space: nowrap;
          padding: 0 8px;
        }
        
        .pagination-select {
          padding: 4px 8px;
          border-radius: 4px;
          border: 1px solid #cbd5e0;
          background: white;
          font-size: 11px;
          min-width: 60px;
        }
      `}</style>

      {/* HEADER SECTION - More Compact */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "flex-start", 
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 16 
        }}>
          <div style={{ flex: 1, minWidth: "300px" }}>
            <h1
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#1a202c",
                margin: "0 0 8px 0",
              }}
            >
              Attendance Defaulters
            </h1>
            <div style={{ 
              display: "flex", 
              flexWrap: "wrap", 
              gap: 8, 
              alignItems: "center",
              fontSize: 13,
              color: "#718096"
            }}>
              <span>Total: <strong>{pagination.totalRecords?.toLocaleString() || "0"}</strong> defaulters</span>
              <span style={{ color: "#cbd5e0" }}>•</span>
              <span>{getDateRangeLabel()}</span>
            </div>
          </div>
          
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                background: showFilters 
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" 
                  : "linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%)",
                color: showFilters ? "white" : "#4a5568",
                border: "none",
                padding: "8px 16px",
                borderRadius: "8px",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
              }}
            >
              {showFilters ? "▲ Hide" : "▼ Filters"}
              <span style={{ 
                backgroundColor: "rgba(255,255,255,0.2)", 
                borderRadius: "50%",
                width: 18,
                height: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11
              }}>
                {countActiveFilters()}
              </span>
            </button>
            
            <button
              onClick={handleDownloadCSV}
              style={{
                background: "linear-gradient(135deg, #48bb78 0%, #38a169 100%)",
                color: "#fff",
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                fontWeight: 500,
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span style={{ fontSize: 16 }}>📥</span>
              <span>Export</span>
            </button>
          </div>
        </div>
        
        {/* Active Filters - More Compact */}
        <div style={{ 
          display: "flex", 
          gap: 8, 
          flexWrap: "wrap",
          marginTop: 12
        }}>
          {selectedDistrict && (
            <span style={{
              background: "#edf2f7",
              padding: "4px 10px",
              borderRadius: "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              color: "#4a5568",
            }}>
              District: {selectedDistrict}
              <button 
                onClick={() => setSelectedDistrict("")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#a0aec0",
                  cursor: "pointer",
                  fontSize: 14,
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          )}
          {selectedMunicipality && (
            <span style={{
              background: "#edf2f7",
              padding: "4px 10px",
              borderRadius: "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              color: "#4a5568",
            }}>
              Municipality: {selectedMunicipality}
              <button 
                onClick={() => setSelectedMunicipality("")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#a0aec0",
                  cursor: "pointer",
                  fontSize: 14,
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          )}
          {(fromDate || toDate) && (
            <span style={{
              background: "#e3f2fd",
              padding: "4px 10px",
              borderRadius: "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              color: "#1976d2",
            }}>
              Dates: {getDateRangeLabel()}
              <button 
                onClick={() => { setFromDate(""); setToDate(""); }}
                style={{
                  background: "none",
                  border: "none",
                  color: "#64b5f6",
                  cursor: "pointer",
                  fontSize: 14,
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          )}
        </div>
      </div>

      {/* FILTERS SECTION - More Compact */}
      {showFilters && (
        <div style={{
          background: "white",
          borderRadius: "12px",
          padding: "16px",
          marginBottom: "20px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
          border: "1px solid #e2e8f0",
        }}>
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", 
            gap: 12,
            marginBottom: 16
          }}>
            <div>
              <label style={{ 
                display: "block", 
                fontSize: 11, 
                color: "#4a5568", 
                marginBottom: 4,
                fontWeight: 500 
              }}>
                District
              </label>
              <select
                value={selectedDistrict}
                onChange={(e) => setSelectedDistrict(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "13px",
                  backgroundColor: "#f8fafc",
                }}
              >
                <option value="">All Districts</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.name}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 11, 
                color: "#4a5568", 
                marginBottom: 4,
                fontWeight: 500 
              }}>
                Municipality
              </label>
              <select
                value={selectedMunicipality}
                onChange={(e) => setSelectedMunicipality(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "13px",
                  backgroundColor: "#f8fafc",
                }}
              >
                <option value="">All Municipalities</option>
                {municipalities
                  .filter((m) => !selectedDistrict || m.district_id === districts.find(d => d.name === selectedDistrict)?.id)
                  .map((m) => (
                    <option key={m.id} value={m.name}>
                      {m.name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 11, 
                color: "#4a5568", 
                marginBottom: 4,
                fontWeight: 500 
              }}>
                Designation
              </label>
              <select
                value={selectedDesignation}
                onChange={(e) => setSelectedDesignation(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "13px",
                  backgroundColor: "#f8fafc",
                }}
              >
                <option value="">All Designations</option>
                {designations.map((d, i) => (
                  <option key={i} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 11, 
                color: "#4a5568", 
                marginBottom: 4,
                fontWeight: 500 
              }}>
                Project Type
              </label>
              <select
                value={selectedProjectType}
                onChange={(e) => setSelectedProjectType(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "13px",
                  backgroundColor: "#f8fafc",
                }}
              >
                <option value="">All Types</option>
                <option value="Park">Park</option>
                <option value="Shelter">Shelter</option>
                <option value="Stretch">Stretch</option>
              </select>
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 11, 
                color: "#4a5568", 
                marginBottom: 4,
                fontWeight: 500 
              }}>
                Search
              </label>
              <input
                type="text"
                value={searchTerm}
                placeholder="Search name or phone..."
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "13px",
                  backgroundColor: "#f8fafc",
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 11, 
                color: "#4a5568", 
                marginBottom: 4,
                fontWeight: 500 
              }}>
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "13px",
                  backgroundColor: "#f8fafc",
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: "block", 
                fontSize: 11, 
                color: "#4a5568", 
                marginBottom: 4,
                fontWeight: 500 
              }}>
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "13px",
                  backgroundColor: "#f8fafc",
                }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                  setSelectedDistrict("");
                  setSelectedMunicipality("");
                  setSelectedDesignation("");
                  setSelectedProjectType("");
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
                style={{
                  background: "#fed7d7",
                  color: "#c53030",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: 13,
                  width: "100%",
                }}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TABLE SECTION - More Compact */}
      <div className="table-container">
        <div style={{ 
          padding: "12px 16px", 
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f8fafc",
          flexWrap: "wrap",
          gap: 8
        }}>
          <div style={{ fontSize: 12, color: "#4a5568" }}>
            <span style={{ fontWeight: 600, color: "#2d3748" }}>Defaulters List</span>
            <span style={{ margin: "0 6px", color: "#cbd5e0" }}>•</span>
            <span>Showing {defaulters.length} of {pagination.totalRecords || 0}</span>
          </div>
          <div style={{ fontSize: 11, color: "#718096", whiteSpace: "nowrap" }}>
            Page {currentPage} of {pagination.totalPages || 1}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "1000px",
              fontSize: "12px",
            }}
          >
            <thead style={{ 
              backgroundColor: "#f1f5f9",
              borderBottom: "1px solid #e2e8f0"
            }}>
              <tr>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "60px" }}>ID</th>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "100px" }}>District</th>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "120px" }}>Municipality</th>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "140px" }}>Name</th>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "100px" }}>Designation</th>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "150px" }} className="mobile-hidden">Email</th>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "100px" }}>Phone</th>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "80px" }}>Project</th>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "100px" }} className="mobile-hidden">Project Code</th>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "70px" }}>Absent Days</th>
                <th style={{ ...thStyle, padding: "12px 8px", textAlign: "left", color: "#4a5568", fontWeight: 600, width: "150px" }}>Absent Dates</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                [...Array(5)].map((_, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    {[...Array(11)].map((_, colIdx) => (
                      <td key={colIdx} style={{ padding: "12px 8px" }}>
                        <div className="loading-shimmer" style={{ height: "16px", borderRadius: "4px" }}></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={11} style={{ padding: "32px 20px", textAlign: "center" }}>
                    <div style={{ 
                      background: "#fed7d7",
                      borderRadius: "8px",
                      padding: "20px",
                      maxWidth: "400px",
                      margin: "0 auto"
                    }}>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#c53030", marginBottom: 8 }}>
                        Error Loading Data
                      </div>
                      <div style={{ fontSize: 12, color: "#742a2a", lineHeight: 1.5 }}>
                        {error}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : defaulters.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ padding: "40px 20px", textAlign: "center" }}>
                    <div style={{ 
                      textAlign: "center", 
                      padding: "40px 20px",
                      background: "#f8fafc",
                      borderRadius: "8px",
                      border: "2px dashed #cbd5e0"
                    }}>
                      <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.7 }}>
                        {fromDate || toDate ? "📭" : "📅"}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: "#4a5568", marginBottom: 8 }}>
                        {fromDate || toDate ? "No Defaulters Found" : "Select Date Range"}
                      </div>
                      <div style={{ fontSize: 13, color: "#718096", maxWidth: "400px", margin: "0 auto", lineHeight: 1.5 }}>
                        {fromDate || toDate 
                          ? "All employees have attended during the selected period." 
                          : "Please select a date range to view attendance defaulters."}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                defaulters.map((def) => (
                  <tr key={def.id} className="table-row">
                    <td style={{ ...tdStyle, padding: "12px 8px", fontFamily: "monospace", color: "#4a5568", fontSize: "11px" }}>
                      <span style={{ 
                        backgroundColor: "#f1f5f9",
                        padding: "3px 6px",
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: 500,
                      }}>
                        {def.id}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, padding: "12px 8px", color: "#4a5568" }} className="compact-cell">
                      {def.district}
                    </td>
                    <td style={{ ...tdStyle, padding: "12px 8px", color: "#4a5568" }} className="compact-cell">
                      {def.municipality}
                    </td>
                    <td style={{ ...tdStyle, padding: "12px 8px", fontWeight: 500, color: "#2d3748" }} className="compact-cell">
                      {def.name}
                    </td>
                    <td style={{ ...tdStyle, padding: "12px 8px", color: "#4a5568" }} className="compact-cell">
                      <span style={{
                        backgroundColor: "#edf2f7",
                        padding: "3px 6px",
                        borderRadius: "8px",
                        fontSize: "11px",
                        fontWeight: 500,
                      }}>
                        {def.designation}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, padding: "12px 8px" }} className="email-cell mobile-hidden">
                      {def.email ? (
                        <a 
                          href={`mailto:${def.email}`} 
                          style={{ 
                            color: "#667eea", 
                            textDecoration: "none",
                            fontWeight: 500,
                            fontSize: "11px",
                          }}
                        >
                          {def.email}
                        </a>
                      ) : (
                        <span style={{ color: "#a0aec0", fontStyle: "italic", fontSize: "11px" }}>-</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, padding: "12px 8px" }}>
                      <a 
                        href={`tel:${def.phone}`} 
                        style={{ 
                          color: "#2d3748", 
                          textDecoration: "none",
                          fontWeight: 500,
                          fontSize: "11px",
                        }}
                      >
                        {def.phone}
                      </a>
                    </td>
                    <td style={{ ...tdStyle, padding: "12px 8px" }}>
                      <span className={`status-badge ${
                        def.project_type === "Park" ? "park-badge" :
                        def.project_type === "Shelter" ? "shelter-badge" :
                        def.project_type === "Stretch" ? "stretch-badge" : "default-badge"
                      }`}>
                        {def.project_type?.charAt(0)}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, padding: "12px 8px", fontFamily: "monospace", fontSize: "10px", color: "#4a5568" }} className="compact-cell mobile-hidden">
                      {def.project_code || "-"}
                    </td>
                    <td style={{ ...tdStyle, padding: "12px 8px" }}>
                      <div style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: def.total_absent_days > 5 
                          ? "linear-gradient(135deg, #fed7d7 0%, #feb2b2 100%)" 
                          : def.total_absent_days > 2 
                          ? "linear-gradient(135deg, #fed7d7 0%, #fbd38d 100%)"
                          : "linear-gradient(135deg, #c6f6d5 0%, #9ae6b4 100%)",
                        color: def.total_absent_days > 5 ? "#c53030" : 
                              def.total_absent_days > 2 ? "#dd6b20" : "#276749",
                        fontWeight: "bold",
                        fontSize: "11px",
                      }}>
                        {def.total_absent_days}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, padding: "8px 4px" }} className="absent-dates-cell">
                      <div className="absent-dates-container">
                        {showAbsentDates[def.id] 
                          ? def.absent_dates?.map((date, idx) => (
                              <span key={idx} className="absent-date-chip">
                                {formatDate(date)}
                              </span>
                            ))
                          : def.absent_dates?.slice(0, 3).map((date, idx) => (
                              <span key={idx} className="absent-date-chip">
                                {formatDate(date)}
                              </span>
                            ))
                        }
                        {def.absent_dates?.length > 3 && !showAbsentDates[def.id] && (
                          <button
                            onClick={() => toggleAbsentDates(def.id)}
                            className="show-more-btn"
                          >
                            +{def.absent_dates.length - 3}
                          </button>
                        )}
                        {showAbsentDates[def.id] && def.absent_dates?.length > 3 && (
                          <button
                            onClick={() => toggleAbsentDates(def.id)}
                            className="show-more-btn"
                          >
                            less
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION - More Compact */}
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#f8fafc",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid #e2e8f0",
            gap: 8,
            flexWrap: "nowrap",
          }}
        >
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              padding: "6px 12px",
              borderRadius: "4px",
              border: "1px solid #cbd5e0",
              background: currentPage === 1 ? "#edf2f7" : "white",
              color: currentPage === 1 ? "#a0aec0" : "#4a5568",
              fontWeight: 500,
              fontSize: "11px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              minWidth: "70px",
              whiteSpace: "nowrap",
            }}
          >
            ← Previous
          </button>

          <div className="pagination-container">
            <span className="pagination-info">Page</span>
            <select
              value={currentPage}
              onChange={(e) => setCurrentPage(Number(e.target.value))}
              className="pagination-select"
            >
              {[...Array(pagination.totalPages || 1)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
            <span className="pagination-info">of {pagination.totalPages || 1}</span>
            <span className="pagination-info" style={{ marginLeft: 8 }}>
              {defaulters.length} of {pagination.totalRecords || 0}
            </span>
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(pagination.totalPages || 1, p + 1))}
            disabled={currentPage === pagination.totalPages}
            style={{
              padding: "6px 12px",
              borderRadius: "4px",
              border: "1px solid #cbd5e0",
              background: currentPage === pagination.totalPages ? "#edf2f7" : "white",
              color: currentPage === pagination.totalPages ? "#a0aec0" : "#4a5568",
              fontWeight: 500,
              fontSize: "11px",
              cursor: currentPage === pagination.totalPages ? "not-allowed" : "pointer",
              minWidth: "70px",
              whiteSpace: "nowrap",
            }}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Simple Info Footer */}
      <div style={{
        marginTop: 24,
        padding: 16,
        background: "#f8fafc",
        borderRadius: "8px",
        borderLeft: "3px solid #667eea",
        fontSize: 12,
        color: "#4a5568",
        lineHeight: 1.5,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>💡</span>
          <div>
            <div style={{ fontWeight: 600, color: "#2d3748", marginBottom: 4 }}>Tips:</div>
            <div>• Use filters to narrow down results • Click "Show more" to view all absent dates • Export CSV for reporting</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParkAttendanceDefaulters;